// frontend/modules/in-app-notifications/stores/in-app-notification-store.ts
import { create } from 'zustand';
import {  NotificationStats, NotificationFilters } from '../types';
import { InAppNotificationDTO } from '@/shared/api/model';

interface InAppNotificationState {
    // State
    notifications: InAppNotificationDTO[];
    selectedNotification: InAppNotificationDTO | null;
    stats: NotificationStats | null;
    filters: NotificationFilters;
    isLoading: boolean;
    error: string | null;
    
    // Pagination
    currentPage: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
    
    // Actions
    setNotifications: (notifications: InAppNotificationDTO[]) => void;
    setSelectedNotification: (notification: InAppNotificationDTO | null) => void;
    setStats: (stats: NotificationStats) => void;
    setFilters: (filters: NotificationFilters) => void;
    setLoading: (loading: boolean) => void;
    setError: (error: string | null) => void;
    
    // Pagination actions
    setCurrentPage: (page: number) => void;
    setPageSize: (size: number) => void;
    setPaginationInfo: (totalElements: number, totalPages: number) => void;
    
    // Notification actions
    markAsRead: (id: number) => void;
    markAsAttended: (id: number) => void;
    removeNotification: (id: number) => void;
    updateNotificationInList: (notification: InAppNotificationDTO) => void;
    
    // Reset
    reset: () => void;
}

const initialState = {
    notifications: [],
    selectedNotification: null,
    stats: null,
    filters: {},
    isLoading: false,
    error: null,
    currentPage: 0,
    pageSize: 20,
    totalElements: 0,
    totalPages: 0,
};

export const useInAppNotificationStore = create<InAppNotificationState>((set, get) => ({
    ...initialState,
    
    // Basic setters
    setNotifications: (notifications) => set({ notifications }),
    setSelectedNotification: (notification) => set({ selectedNotification: notification }),
    setStats: (stats) => set({ stats }),
    setFilters: (filters) => set({ filters }),
    setLoading: (loading) => set({ isLoading: loading }),
    setError: (error) => set({ error }),
    
    // Pagination setters
    setCurrentPage: (page) => set({ currentPage: page }),
    setPageSize: (size) => set({ pageSize: size }),
    setPaginationInfo: (totalElements, totalPages) => set({ totalElements, totalPages }),
    
    // Notification actions
    markAsRead: (id: number) => {
        set((state) => ({
            ...state,
            notifications: state.notifications.map(notification =>
                notification.id === id
                    ? { ...notification, read: true, readAt: new Date().toISOString() }
                    : notification
            ),
            stats: state.stats ? {
                ...state.stats,
                unreadCount: Math.max(0, state.stats.unreadCount - 1)
            } : state.stats
        }));
        
        // Update selected notification if it's the same one
        const { selectedNotification } = get();
        if (selectedNotification && selectedNotification.id === id) {
            set({
                selectedNotification: {
                    ...selectedNotification,
                    read: true,
                    readAt: new Date().toISOString()
                }
            });
        }
    },
    
    markAsAttended: (id: number) => {
        set((state) => ({
            ...state,
            notifications: state.notifications.map(notification =>
                notification.id === id
                    ? { ...notification, attended: true, attendedAt: new Date().toISOString() }
                    : notification
            ),
            stats: state.stats ? {
                ...state.stats,
                attendedCount: state.stats.attendedCount + 1
            } : state.stats
        }));
        
        // Update selected notification if it's the same one
        const { selectedNotification } = get();
        if (selectedNotification && selectedNotification.id === id) {
            set({
                selectedNotification: {
                    ...selectedNotification,
                    attended: true,
                    attendedAt: new Date().toISOString()
                }
            });
        }
    },
    
    removeNotification: (id) => {
        const { notifications } = get();
        const updatedNotifications = notifications.filter(notification => notification.id !== id);
        set({ notifications: updatedNotifications });
        
        // Clear selected notification if it's the one being removed
        const { selectedNotification } = get();
        if (selectedNotification && selectedNotification.id === id) {
            set({ selectedNotification: null });
        }
    },
    
    updateNotificationInList: (updatedNotification) => {
        const { notifications } = get();
        const updatedNotifications = notifications.map(notification =>
            notification.id === updatedNotification.id ? updatedNotification : notification
        );
        set({ notifications: updatedNotifications });
        
        // Update selected notification if it's the same one
        const { selectedNotification } = get();
        if (selectedNotification && selectedNotification.id === updatedNotification.id) {
            set({ selectedNotification: updatedNotification });
        }
    },
    
    // Reset store
    reset: () => set(initialState),
}));
